import {AppTourService} from '../../services/app-tour/app-tour.service';
import {cloneDeep} from 'lodash';
import {ActivatedRoute, Params} from '@angular/router';
import {firstValueFrom, Observable} from 'rxjs';
import {Select, Store} from '@ngxs/store';
import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {ICalorieTracking} from '../../models/meal-plans';
import {CalorieTrackingNamespace, MealPlanBuilderAction, MealPlanBuilderState} from '../../models/meal-plan-builder';
import {takeWhile} from 'rxjs/operators';
import {MeasurementAction, MeasurementState} from '../../models/measurement';
import {UtilitiesService} from '../../services/utilities-service/utilities.service';
import {IService, ServiceState} from '../../models/service';


@Component({
  selector: 'app-calorie-tracking',
  templateUrl: './calorie-tracking.component.html',
  styleUrls: ['./calorie-tracking.component.scss']
})
export class CalorieTrackingComponent implements OnInit, OnChanges, OnDestroy {

  constructor(
    private store: Store,
    private utilitiesServiceService: UtilitiesService,
    private activatedRoute: ActivatedRoute,
    private appTourService: AppTourService
  ) { }
  @Select(MealPlanBuilderState.getCalorieTracking) calorieTracking$: Observable<Partial<CalorieTrackingNamespace.ICalorieTracking> | null>;

  @Input() trackingValue: 'deficit' | 'surplus' = 'deficit';
  @Input() calories = 0;
  @Input() mealPlanCalories = 0;
  @Output() changeCalories: EventEmitter<Partial<ICalorieTracking>> = new EventEmitter();
  @Output() withoutData: EventEmitter<string> = new EventEmitter();
  activeObservables: boolean;
  measurementType: CalorieTrackingNamespace.MeasurementType = null;
  gebValue = 0;
  tmbValue = 0;
  getValue = 0;

  measurementCalories: number; // Get value selected (gebValue | tmbValue | getValue)


  /**
   * @property use for input validation
   */
  mathInfinity = Infinity;

  formatterKcal = (value: number | null): string => {
    if (!value) return `${0} kcal`;

    return `${Number(value.toFixed(2))} kcal`;
  }

  parserKcal = (value: string): string => value.replace(' kcal', '');

  ngOnInit(): void {
    this.initValues();
    this.initValuesHttp().then();
    this.listeners();
  }

  private initValues(): void {
    this.measurementCalories = 0;
    this.activeObservables = true;
  }

  private listeners(): void {
    this.calorieTracking$.pipe(takeWhile(() => this.activeObservables)).subscribe({
      next: (calorieTracking: Partial<CalorieTrackingNamespace.ICalorieTracking> | null): void => {
        if (!calorieTracking) return;
        this.calories = calorieTracking.calories;
        this.trackingValue = calorieTracking.trackingType;
        this.measurementType = calorieTracking.measurementType;
        // this.measurementType = 'tmb';
      }
    });

    this.appTourService.events.pipe(takeWhile(() => this.activeObservables)).subscribe({
      next: (event: string) => {
        switch (event) {
          case 'setMeasurementValue': {
            this.measurementType = 'tmb';
            break;
          }
          case 'setCaloriesValue': {
            this.calories = 150;
            break;
          }
        }
      }
    });
  }

  async initValuesHttp(): Promise<void> {
    const params: Params = this.utilitiesServiceService.getAllParams(this.activatedRoute);
    const service: IService = await firstValueFrom(this.store.selectOnce(ServiceState.getActive));
    if (!service) {
      this.withoutData.emit('No se tiene ningún servicio con el paciente.');
      return;
    }
    try {
      let measurements = cloneDeep(await firstValueFrom(this.store.selectOnce(MeasurementState.getMeasurementsHistorialHeaders)));
      if (measurements.length === 0) {
        await firstValueFrom(this.store.dispatch(new MeasurementAction.GetPatientMeasurementHeaders(params?.patientId, service?._id)));
        measurements = cloneDeep(await firstValueFrom(this.store.selectOnce(MeasurementState.getMeasurementsHistorialHeaders)));
      }
      this.gebValue = measurements.find(measurement => measurement.name === 'geb')?.lastValue || 0;
      this.tmbValue = measurements.find(measurement => measurement.name === 'tmb')?.lastValue || 0;
      this.getValue = measurements.find(measurement => measurement.name === 'get')?.lastValue || 0;

      if (!this.gebValue && !this.tmbValue && !this.getValue) {
        this.withoutData.emit('Para realizar el seguimiento de calorías es requerida al menos una de las mediciones: Tasa metabólica basal, gasto energético basal y/o gasto energético total.');
        return;
      }
      if (this.measurementType) {
        await this.onChange();
      }
    } catch (e) {
      console.log(e);
    }
  }

  async onChange(): Promise<void> {
    this.measurementCalories = this.measurementType === 'geb' ? this.gebValue : this.measurementType === 'tmb' ? this.tmbValue : this.getValue;
    const value = this.trackingValue === 'deficit' ? this.measurementCalories - this.calories : this.measurementCalories + this.calories;
    let message = '';
    if (this.trackingValue === 'deficit') {
      message = Math.trunc(value) > Math.trunc(this.mealPlanCalories) ?
        `Las calorías del plan alimenticio están ${Math.trunc(value) - Math.trunc(this.mealPlanCalories)} kcal por debajo del déficit calórico deseado.` :
        Math.trunc(value) < Math.trunc(this.mealPlanCalories) ?
          `Las calorías en el plan alimenticio están ${Math.trunc(this.mealPlanCalories) - Math.trunc(value)} kcal por encima del déficit calórico deseado.` :
          '';

      if (this.calories > this.measurementCalories) {
        this.calories = Number(this.measurementCalories.toFixed(2));
      }

    } else if (this.trackingValue === 'surplus') {
      message = Math.trunc(value) > Math.trunc(this.mealPlanCalories) ?
        `Las calorías del plan alimenticio están ${Math.trunc(value) - Math.trunc(this.mealPlanCalories)} kcal por debajo del superávit calórico deseado.` :
        Math.trunc(value) < Math.trunc(this.mealPlanCalories) ?
          `Las calorías en el plan alimenticio están ${Math.trunc(this.mealPlanCalories) - Math.trunc(value)} kcal por encima del superávit calórico deseado.` :
          '';
    }
    const obj: Partial<ICalorieTracking> = {
      calories: this.calories,
      message,
      type: this.measurementType,
      measurementCalories: this.measurementCalories,
      mealPlanCalories: this.mealPlanCalories,
      caloriesAdded: Math.trunc(value),
      trackingType: this.trackingValue
    };
    this.changeCalories.emit(obj);




    if (this.measurementType !== null) {
      await firstValueFrom(
        this.store.dispatch(new MealPlanBuilderAction.PatchCalorieTracking({
          measurementType: this.measurementType,
          calories: this.calories,
          trackingType: this.trackingValue,
        }))
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.mealPlanCalories) {
      this.onChange().then();
    }
  }

  /**
   * Get calorie tracking value
   */
  getCalcCalorieTracking(): number {
    const valueSelected = this.getValueSelected();

    if (this.trackingValue === 'deficit') {
      return valueSelected - this.calories;
    } else {
      return valueSelected + this.calories;
    }
  }

  /**
   * Get value selected in select element
   * @private
   */
  private getValueSelected(): number {
    if (this.measurementType === 'geb') {
      return this.gebValue;
    } else if (this.measurementType === 'get') {
      return this.getValue;
    } else if (this.measurementType === 'tmb') {
      return this.tmbValue;
    } else {
      return 0;
    }
  }

  ngOnDestroy(): void {
    this.activeObservables = false;
  }
}
